<template>
  <b-modal
    v-model="open"
    :title="isEdit ? 'Editar lançamento' : 'Novo lançamento'"
    size="lg"
    no-close-on-backdrop
    @hide="onHide">
    <form @submit.prevent="submit">
      <div class="row">
        <div class="col-sm-4 form-group">
          <label>Data <span class="text-danger">*</span></label>
          <input type="date" class="form-control" v-model="form.transaction_date" required>
        </div>
        <div class="col-sm-4 form-group">
          <label>Categoria</label>
          <select class="custom-select" v-model="form.category">
            <option value="">—</option>
            <option v-for="c in activeCategories" :key="c.slug" :value="c.slug">{{ c.name }}</option>
          </select>
        </div>
        <div class="col-sm-4 form-group">
          <label>Forma de pagamento <span class="text-danger">*</span></label>
          <select class="custom-select" v-model="form.payment_method" required>
            <option value="" disabled>Selecione</option>
            <option v-for="p in activePaymentMethods" :key="p.slug" :value="p.slug">{{ p.name }}</option>
          </select>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12 form-group">
          <label>Descrição <span class="text-danger">*</span></label>
          <input type="text" class="form-control" v-model="form.description" maxlength="255" required>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-3 form-group">
          <label>Moeda <span class="text-danger">*</span></label>
          <select class="custom-select" v-model="form.currency" required>
            <option v-for="c in enabledCurrencies" :key="c" :value="c">{{ c }}</option>
          </select>
        </div>
        <div class="col-sm-3 form-group">
          <label>Valor original <span class="text-danger">*</span></label>
          <money
            class="form-control text-right"
            v-model="form.amount_original"
            v-bind="moneyConfig" />
        </div>
        <div class="col-sm-3 form-group" v-if="needsExchangeRate">
          <label>Câmbio <span class="text-danger">*</span></label>
          <money
            class="form-control text-right"
            v-model="form.exchange_rate"
            v-bind="exchangeConfig" />
        </div>
        <div class="col-sm-3 form-group">
          <label>Valor base ({{ baseCurrency }})</label>
          <input type="text" class="form-control text-right" :value="calculatedBaseLabel" readonly>
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12 form-group">
          <div class="custom-control custom-checkbox">
            <input
              type="checkbox"
              class="custom-control-input"
              id="payer_is_participant"
              v-model="payerIsParticipant">
            <label class="custom-control-label" for="payer_is_participant">
              Pagador é o próprio participante
            </label>
          </div>
        </div>
        <div class="col-sm-6 form-group">
          <label>Pagador</label>
          <input type="text" class="form-control" v-model="form.payer_name" :disabled="payerIsParticipant">
        </div>
        <div class="col-sm-6 form-group">
          <label>Documento do pagador</label>
          <input
            type="text"
            class="form-control"
            v-model="form.payer_document"
            v-mask="['###.###.###-##', '##.###.###/####-##']"
            :disabled="payerIsParticipant"
            placeholder="CPF ou CNPJ">
        </div>
      </div>

      <div class="row">
        <div class="col-sm-12 form-group">
          <label>Documento financeiro</label>
          <input type="text" class="form-control" v-model="form.financial_document" placeholder="NF, recibo, ID externo...">
        </div>
        <div class="col-sm-12 form-group">
          <label>Observações</label>
          <textarea class="form-control" rows="4" v-model="form.notes"></textarea>
        </div>
      </div>
    </form>

    <template #modal-footer>
      <b-button variant="secondary" @click="close">Cancelar</b-button>
      <b-button variant="primary" :disabled="saving" @click="submit">
        {{ saving ? 'Salvando...' : 'Salvar' }}
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import { Money } from 'v-money'
import services from './services'
import { errorMessage, successMessage } from '@/components/alertsMessages'

const emptyForm = () => ({
  transaction_date: new Date().toISOString().slice(0, 10),
  category: '',
  payment_method: '',
  description: '',
  currency: 'BRL',
  amount_original: 0,
  exchange_rate: null,
  payer_name: '',
  payer_document: '',
  financial_document: '',
  notes: ''
})

export default {
  name: 'FinancialFormModal',
  components: { Money },
  props: {
    value: { type: Boolean, default: false },
    participantId: { type: [Number, String], required: true },
    record: { type: Object, default: null },
    settings: { type: Object, default: () => ({}) },
    participant: { type: Object, default: () => ({}) }
  },
  data () {
    return {
      open: this.value,
      saving: false,
      payerIsParticipant: false,
      form: emptyForm(),
      moneyConfig: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: '',
        precision: 2,
        masked: false
      },
      exchangeConfig: {
        decimal: ',',
        thousands: '.',
        prefix: '',
        suffix: '',
        precision: 6,
        masked: false
      }
    }
  },
  computed: {
    isEdit () {
      return !!(this.record && this.record.id)
    },
    baseCurrency () {
      return this.settings.base_currency || 'BRL'
    },
    enabledCurrencies () {
      return (this.settings.enabled_currencies && this.settings.enabled_currencies.length)
        ? this.settings.enabled_currencies
        : [this.baseCurrency]
    },
    activeCategories () {
      return (this.settings.categories || []).filter(c => c.active)
    },
    activePaymentMethods () {
      return (this.settings.payment_methods || []).filter(p => p.active)
    },
    needsExchangeRate () {
      return this.form.currency && this.form.currency !== this.baseCurrency
    },
    calculatedBase () {
      const amt = Number(this.form.amount_original) || 0
      if (!this.needsExchangeRate) return amt
      const rate = Number(this.form.exchange_rate) || 0
      return amt * rate
    },
    calculatedBaseLabel () {
      return this.calculatedBase.toFixed(2)
    }
  },
  watch: {
    value (val) {
      this.open = val
      if (val) this.hydrate()
    },
    payerIsParticipant (val) {
      if (val) {
        this.form.payer_name = this.participant.name || ''
        this.form.payer_document = this.participant.cpf || ''
      }
    }
  },
  methods: {
    hydrate () {
      if (this.record && this.record.id) {
        this.form = {
          transaction_date: this.record.transaction_date,
          category: this.record.category || '',
          payment_method: this.record.payment_method || '',
          description: this.record.description || '',
          currency: this.record.currency || this.baseCurrency,
          amount_original: Number(this.record.amount_original) || 0,
          exchange_rate: this.record.exchange_rate !== null ? Number(this.record.exchange_rate) : null,
          payer_name: this.record.payer_name || '',
          payer_document: this.record.payer_document || '',
          financial_document: this.record.financial_document || '',
          notes: this.record.notes || ''
        }
        this.payerIsParticipant = (
          this.form.payer_name === (this.participant.name || '') &&
          this.form.payer_document === (this.participant.cpf || '')
        )
      } else {
        this.form = emptyForm()
        this.form.currency = this.baseCurrency
        this.payerIsParticipant = false
      }
    },
    close () {
      this.open = false
    },
    onHide () {
      this.$emit('input', false)
    },
    async submit () {
      if (this.needsExchangeRate && (!this.form.exchange_rate || this.form.exchange_rate <= 0)) {
        errorMessage('Câmbio obrigatório quando a moeda é diferente da moeda base.', 3000)
        return
      }
      this.saving = true
      try {
        const payload = { ...this.form }
        if (!this.needsExchangeRate) payload.exchange_rate = null

        if (this.isEdit) {
          await services.updateFinancial(this.participantId, this.record.id, payload)
          successMessage('Lançamento atualizado.', 2000)
        } else {
          await services.storeFinancial(this.participantId, payload)
          successMessage('Lançamento criado.', 2000)
        }
        this.$emit('saved')
        this.close()
      } catch (e) {
        const msg = e?.response?.data?.message || 'Falha ao salvar lançamento.'
        errorMessage(msg, 3000)
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
